const isArray = Array.isArray

/**
 * Check if given value is a valid array with at least 1 entry.
 *
 * @TODO [ATL-7226]: Replace all occurences in atlantis with this generic wrapper
 * because this check is done more than once.
 *
 * @example
 * isValidArray() === false
 * isValidArray([]) === false
 * isValidArray(['foo']) === true
 *
 * @param {*} array - The value to be checked
 * @returns {boolean} Whether the given value is a valid array or not
 */
function isValidArray(array) {
    return isArray(array) && array.length > 0
}

/**
 * Filter an array to get only unique values and remove all duplicates.
 *
 * @example
 * const uniqueArray = arrayWithDuplicates.filter(filterUniqueArray)
 *
 * @param {*} element - The current element being processed in the array
 * @param {number} index - The index of the current element being processed in the array
 * @param {Array} array - The array filter was called upon
 * @returns {Array} The filtered array with unique values
 */
function filterUniqueArray(element, index, array) {
    return array.indexOf(element) === index
}

/**
 * Converts the array of objects to a map of key => object, where the key is taken from the idFieldName field.
 *
 * @param {Array} array - The array of objects
 * @param {string} idFieldName - The name of the field in the array to use as a key
 * @returns {Object}
 */
function arrayToObject(array, idFieldName) {
    if (!isValidArray(array)) {
        return {}
    }

    return array.reduce((result, element) => {
        result[element[idFieldName]] = element
        return result
    }, {})
}

/**
 * Convert array entries to string.
 *
 * @example
 * arrayToString() === ''
 * arrayToString(['a', 'b']) === 'a b'
 * arrayToString(['a', 'b'], ';') === 'a;b'
 *
 * @param {Array} array - The array to be combined
 * @param {string} [separator=' '] - String to separate each pair of adjacent elements of the array
 * @returns {string} The joined array as string
 */
function arrayToString(array, separator = ' ') {
    if (!isValidArray(array)) {
        return ''
    }
    return array.join(separator)
}

/**
 * Convenient helper to get first selected item from datagrid table.
 *
 * @example
 * getArrayItem() === null
 * getArrayItem(['a', 'b']) === 'a'
 * getArrayItem(['a', 'b'], 1) === 'b'
 *
 * @param {Array} [array] - The selection list
 * @param {number} [index=0] - The array's item index
 * @returns {*|null} The first selected array item or null
 */
function getArrayItem(array, index = 0) {
    if (!isValidArray(array)) {
        return null
    }

    return array[index] !== undefined ? array[index] : null
}

/**
 * Add array item at given index.
 *
 * @example
 * addArrayItem([1, 2, 3], 4) === [4, 1, 2, 3]
 * addArrayItem([1, 2, 3], 4, 2) === [1, 2, 4, 3]
 *
 * @param {Array} [array=[]] - The list to be enhanced
 * @param {*} [value] - The value to be added
 * @param {number} [index=0] - The values position in array
 * @returns {Array} The newly created array
 */
function addArrayItem(array = [], value, index = 0) {
    const result = [].concat(array)
    result.splice(index, 0, value)

    return result
}

/**
 * Replace an array item at given position without mutation.
 *
 * @example
 * replaceArrayItem([1, 2, 3], 4) === [4, 2, 3]
 * replaceArrayItem([1, 2, 3], 4, 2) === [1, 2, 4]
 *
 * @param {Array} [array=[]] - The list to be adjusted
 * @param {*} [value] - The value to be replaced
 * @param {number} [index=0] - The values position in array
 * @returns {Array} The newly created array
 */
function replaceArrayItem(array = [], value, index = 0) {
    const result = [].concat(array)
    const length = result.length
    const position = index > length - 1 ? length - 1 : index

    result[position] = value

    return result
}

/**
 * Fill all the elements of an array from a start index to an end index
 * with a static value.
 *
 * @param {Array} [array=[]] - The list to be adjusted
 * @param {*} [value=null] - The value to be filled in
 * @param {number} [arrayLength] - The array's length
 * @returns {Array} The filled array
 */
function fillArray(array = [], value = null, arrayLength) {
    const length = arrayLength !== undefined ? arrayLength : (array && array.length) || 0

    if (!isValidArray(array)) {
        return Array.from({ length }, () => value)
    }

    return Array.from(array.fill.call({ length }, value))
}

/**
 * Simple helper to ensure we have an array type.
 *
 * @param {*} source - The type to be checked/converted
 * @returns {Array<*>}
 */
function toArray(source) {
    if (source === undefined || source === null) {
        return []
    }
    if (isArray(source)) {
        return source
    }
    if (source && source.length) {
        return Array.from(source)
    }

    return [source]
}

/**
 * Returns an array with arrays of the given size.
 *
 * @example
 * chunkArray([1, 2, 3, 4, 5, 6], 2) === [[1, 2], [3, 4], [5, 6]]
 *
 * @see {@link https://ourcodeworld.com/articles/read/278/how-to-split-an-array-into-chunks-of-the-same-size-easily-in-javascript}
 *
 * @param {Array} [array=[]] - Array to be splitted
 * @param {number} [chunkSize=20] - Size of every group
 * @returns {Array} The chunked array
 */
function chunkArray(array = [], chunkSize = 20) {
    if (!isValidArray(array)) {
        return []
    }

    if (chunkSize <= 0) {
        return array
    }

    const clonedArray = array.slice()
    const chunkedArray = []

    while (clonedArray.length) {
        chunkedArray.push(clonedArray.splice(0, chunkSize))
    }

    return chunkedArray
}

export {
    chunkArray,
    addArrayItem,
    arrayToObject,
    arrayToString,
    fillArray,
    filterUniqueArray,
    getArrayItem,
    isValidArray,
    replaceArrayItem,
    toArray
}
